/** Ask aloud: the only way to create a question during a lesson (UX §7.6, §8.5).
 *
 * The owner taught with the old screen and called the structured editor inside it "a terrible
 * form very difficult to even use". They were right: six item types, option rows, misconception
 * fields, Bloom, difficulty, tags and image upload, in a 320 px column, while a class waits.
 * Authoring is a planning act and needs a desk. What a lecturer actually needs at the lectern is
 * this: a shape, an objective, a timer, and a note nobody else will read.
 *
 * Four controls and a button. No AI, no dictation, no option text, no correct answer.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { LO } from "../teacher/api";
import { Button, Sheet } from "../ui";
import { Keycap } from "./Keycap";

export type Shape = "abcd" | "ab" | "yesno";
const KEYS: Record<Shape, string[]> = { abcd: ["A", "B", "C", "D"], ab: ["A", "B"], yesno: ["Y", "N"] };
const TIMERS = [20, 30, 60, 0];

/** Focus starts on the shape chooser: it is the first decision and the only required one. */
const chosenShape = (box: HTMLElement) => box.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]');

type Props = {
  los: LO[];
  defaultLoId: string | null;
  defaultTimer: number;
  /** Why nothing may be asked — an i18n key — or null. A question is open, or the session
   * has ended and the engine refuses `open_item` from here on. */
  blocked: string | null;
  onClose: () => void;
  onAsk: (v: { keys: string[]; shape: Shape; loId: string | null; timerS: number; note: string }) => void;
};

export function AskAloudSheet({ los, defaultLoId, defaultTimer, blocked, onClose, onAsk }: Props) {
  const { t } = useTranslation();
  const [shape, setShape] = useState<Shape>("abcd");
  const [loId, setLoId] = useState<string>(defaultLoId ?? "");
  const [timerS, setTimerS] = useState<number>(defaultTimer);
  const [note, setNote] = useState("");

  const submit = () => {
    if (blocked) return;
    onAsk({ keys: KEYS[shape], shape, loId: loId || null, timerS, note: note.trim() });
  };

  return (
    <Sheet
      title={t("live.aloud.title")}
      lead={t("live.aloud.lead")}
      onClose={onClose}
      initialFocus={chosenShape}
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold">{t("live.aloud.shape")}</legend>
          <div role="radiogroup" aria-label={t("live.aloud.shape")} className="grid-fit" style={{ "--fit-min": "7rem", gap: "0.5rem" } as React.CSSProperties}>
            {(Object.keys(KEYS) as Shape[]).map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={shape === s}
                tabIndex={shape === s ? 0 : -1}
                onClick={() => setShape(s)}
                onKeyDown={(e) => {
                  const all = Object.keys(KEYS) as Shape[];
                  if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                    e.preventDefault();
                    setShape(all[(all.indexOf(s) + 1) % all.length]!);
                  }
                  if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                    e.preventDefault();
                    setShape(all[(all.indexOf(s) + all.length - 1) % all.length]!);
                  }
                }}
                className={[
                  "flex min-h-[4.5rem] items-center justify-center rounded-[var(--radius-card)] border-2 px-3 font-semibold",
                  shape === s ? "border-[color:var(--accent)] bg-[color:var(--surface-2)]" : "border-[color:var(--line)]",
                ].join(" ")}
              >
                {s === "yesno" ? t("live.quick.yesnoLabel") : KEYS[s].join(" ")}
              </button>
            ))}
          </div>
        </fieldset>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-semibold">{t("live.aloud.objective")}</span>
          <select className="field" value={loId} onChange={(e) => setLoId(e.target.value)}>
            <option value="">{t("live.aloud.noObjective")}</option>
            {los.map((lo) => (
              <option key={lo.lo_id} value={lo.lo_id}>{`${lo.code} ${lo.text}`}</option>
            ))}
          </select>
          <span className="text-[color:var(--ink-2)]">{t("live.aloud.objectiveHint")}</span>
        </label>

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold">{t("live.timer")}</legend>
          <div className="grid-fit" style={{ "--fit-min": "7rem", gap: "0.5rem" } as React.CSSProperties}>
            {TIMERS.map((s) => (
              <Button key={s} variant={timerS === s ? "primary" : "secondary"} aria-pressed={timerS === s} onClick={() => setTimerS(s)}>
                {s === 0 ? t("live.aloud.noTimer") : t("live.seconds", { n: s })}
              </Button>
            ))}
          </div>
        </fieldset>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-semibold">{t("live.aloud.note")}</span>
          <input className="field" value={note} maxLength={140} onChange={(e) => setNote(e.target.value)} />
          {/* Not a promise, a fact: the server blanks the stem of a spoken item before it
              reaches a student's device (session_service.hub._student_item). */}
          <span className="text-[color:var(--ink-2)]">{t("live.aloud.noteHint")}</span>
        </label>

        <div className="flex flex-col gap-2">
          <Button block type="submit" disabled={!!blocked} title={blocked ? t(blocked) : undefined}>
            <span>{t("live.aloud.ask")}</span>
            <Keycap keys={["Enter"]} />
          </Button>
          {blocked && <p role="alert" className="text-sm" style={{ color: "var(--danger)" }}>{t(blocked)}</p>}
          <Button block variant="secondary" onClick={onClose}>
            <span>{t("dialog.cancel")}</span>
            <Keycap keys={["Esc"]} />
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
